import React, { forwardRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { CascadingSelectInlineEditViewIsEditable } from '@atlassian/jira-issue-field-cascading-select-inline-edit-full/src/ui/cascading-select/index.tsx';
import type { CascadingSelectCell$key } from '@atlassian/jira-relay/src/__generated__/CascadingSelectCell.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useIsInlineEditingExtendedFieldSupportEnabled,
	useIsSingleLineRowHeightEnabled,
} from '../../../../controllers/features/selectors.tsx';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';
import { INLINE_EDIT_ANALYTICS_ATTRIBUTES } from '../common/inline-edit-analytics-attributes.tsx';
import { DUPLICATE_FIELD_EDIT_WIDTH } from '../../../constants.tsx';

export type Props = {
	fieldRef: CascadingSelectCell$key;
	isFirstField?: boolean;
	hasDuplicates?: boolean;
};

export const CascadingSelectCell = ({ fieldRef, isFirstField, hasDuplicates }: Props) => {
	const data = useFragment<CascadingSelectCell$key>(
		graphql`
			fragment CascadingSelectCell on JiraCascadingSelectField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
				...cascadingSelect_issueFieldCascadingSelectInlineEditFull_CascadingSelectInlineEditViewIsEditable_fragmentRef
			}
		`,
		fieldRef,
	);

	const isInlineEditingExtendedFieldSupportEnabled =
		useIsInlineEditingExtendedFieldSupportEnabled();

	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	const isEditable =
		'lazyIsEditableInIssueView' in data && isInlineEditingExtendedFieldSupportEnabled
			? data.lazyIsEditableInIssueView
			: false;
	useLazyLoadEditability(data.id, isEditable);

	const shouldRenderHoverPopover = useIsSingleLineRowHeightEnabled();

	if (hasDuplicates && shouldRenderHoverPopover) {
		return (
			<CascadingSelectInlineEditViewIsEditable
				attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
				spacing="default"
				editViewPopup
				editViewPopupAlignBlock="start"
				editViewPopupWidth={DUPLICATE_FIELD_EDIT_WIDTH}
				// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
				editViewPopupTarget={isFirstField ? CustomTarget : undefined}
				fragmentRef={data}
				isEditable={isEditable ?? false}
				readViewFitContainerHeight
				menuPosition={isFirstField ? 'fixed' : 'absolute'}
				isTruncated
				shouldRenderHoverPopover={isFirstField && shouldRenderHoverPopover}
			/>
		);
	}

	return (
		<CascadingSelectInlineEditViewIsEditable
			attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
			spacing="default"
			editViewPopup
			editViewPopupAlignBlock="start"
			// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
			editViewPopupTarget={CustomTarget}
			fragmentRef={data}
			isEditable={isEditable ?? false}
			readViewFitContainerHeight
			menuPosition="fixed"
			isTruncated
			shouldRenderHoverPopover={shouldRenderHoverPopover}
		/>
	);
};

const CustomTarget = forwardRef<HTMLDivElement, {}>((_, ref) => {
	return (
		<Box
			xcss={fg('nin_issue_table_rtl_fix') ? customTargetStyles : customTargetStylesOld}
			ref={ref}
		/>
	);
});

const customTargetStylesOld = xcss({
	width: '0',
	position: 'absolute',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	top: 'space.0',
});

const customTargetStyles = xcss({
	width: '0',
	position: 'absolute',
	insetBlockStart: 'space.0',
});
